import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useForm } from '../../../pages/General/useForm';
import FormHelperText from '@mui/material/FormHelperText';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import CircularProgress from '@mui/material/CircularProgress';
import Backdrop from '@mui/material/Backdrop';
import LinearProgress from '@mui/material/LinearProgress';
import Autocomplete from "@mui/material/Autocomplete";
import { fetchAddressBilling, updateAddressBilling, storeAddressBilling } from './addressBillingSlice';
import { fetchCountry, fetchState } from '../../../pages/General/countryStateCitySlice';

// Reusable standard configuration styling dictionary for enterprise outlined inputs
const enterpriseBillingInputStyles = {
    width: '100%',
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: '#FFFFFF',
        transition: 'all 0.2s ease-in-out',
        border: '1px solid #E2E8F0',
        '& fieldset': { border: 'none' },
        '&:hover': {
            borderColor: '#CBD5E1',
            boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
        },
        '&.Mui-focused': {
            borderColor: '#1976d2', // --- CORPORATE BLUE IDENTITY TOKEN ---
            boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.08)'
        }
    },
    '& .MuiInputLabel-outlined': {
        color: '#64748B',
        fontWeight: 500,
        transform: 'translate(14px, 10px) scale(1)',
        '&.Mui-focused, &.MuiInputLabel-shrink': {
            transform: 'translate(14px, -8px) scale(0.75)',
            color: '#1976d2',
            fontWeight: 600
        }
    },
    '& input, & .MuiSelect-select': {
        padding: '10px 14px !important',
        fontSize: '13.5px',
        color: '#1E293B',
        fontWeight: 500
    },
    '& .MuiFormHelperText-root': {
        mx: 0.5,
        mt: 0.75,
        fontWeight: 500
    }
};

const AddressBillingPage = () => {
    const dispatch = useDispatch();
    const addressbilling = useSelector(state => state.addressbilling);
    const country = useSelector(state => state.countrystate.country);
    const state = useSelector(state => state.countrystate.state);
    const profile = useSelector(state => state.auth.profile); 
    const [alert, setAlert] = useState(false);
    const [open, setOpen] = useState(false);
    const [checkFormSubmit, setCheckFormSubmit] = useState(false);
    const handleClose = () => {
        setOpen(false)
    };

    const initialFValues = {
        billing_name: '',
        billing_address: '',
        billing_address2: '',
        billing_city: '',
        billing_company: '',
        billing_country_code: '',
        billing_email: '',
        billing_phone: '',
        billing_postal_code: '',
        billing_state_code: ''
    }

    useEffect(() => {
        dispatch(fetchAddressBilling());
        if (addressbilling && addressbilling.data && addressbilling.data.billing_name) {
            setValues({
                ...values,
                billing_name: addressbilling && addressbilling.data && addressbilling.data.billing_name,
                billing_address: addressbilling && addressbilling.data && addressbilling.data.billing_address,
                billing_address2: addressbilling && addressbilling.data && addressbilling.data.billing_address2,
                billing_city: addressbilling && addressbilling.data && addressbilling.data.billing_city,
                billing_company: addressbilling && addressbilling.data && addressbilling.data.billing_company,
                billing_email: addressbilling && addressbilling.data && addressbilling.data.billing_email,
                billing_phone: addressbilling && addressbilling.data && addressbilling.data.billing_phone,
                billing_country_code: addressbilling && addressbilling.data && addressbilling.data.billing_country_code,
                billing_postal_code: addressbilling && addressbilling.data && addressbilling.data.billing_postal_code,
                billing_state_code: addressbilling && addressbilling.data && addressbilling.data.billing_state_code,
            })
        }
    }, [addressbilling && addressbilling.data && addressbilling.data.billing_name]);

    useEffect(() => {
        if (checkFormSubmit && addressbilling && addressbilling.message) {
            setAlert(true)
            setOpen(true)
        } else if (checkFormSubmit && addressbilling && addressbilling.hasErrors == true) {
            setAlert(true)
            setOpen(true)
        }
    }, [dispatch, addressbilling && addressbilling.message])

    const validate = (fieldValues = values) => {
        let temp = { ...errors }
        if ('billing_name' in fieldValues)
            temp.billing_name = fieldValues.billing_name ? "" : "This field is required."
        if (fieldValues && fieldValues.billing_name && fieldValues.billing_name.length < 3) {
            temp.billing_name = "Billing Name required minimum 3 characters"
        }
        if ('billing_address' in fieldValues)
            temp.billing_address = fieldValues.billing_address ? "" : "This field is required."
        if ('billing_city' in fieldValues)
            temp.billing_city = fieldValues.billing_city ? "" : "This field is required."
        if (fieldValues && fieldValues.billing_city && fieldValues.billing_city.length < 3)
            temp.billing_city = "Billing City required minimum 3 characters"
        if (fieldValues && fieldValues.billing_phone && fieldValues.billing_phone.length < 10)
            temp.billing_phone = "Billing Phone required minimum 10 digits"
        if ('billing_company' in fieldValues)
            temp.billing_company = fieldValues.billing_company ? "" : "This field is required."
        if ('billing_country_code' in fieldValues)
            temp.billing_country_code = fieldValues.billing_country_code ? "" : "This field is required."
        if ('billing_email' in fieldValues)
            temp.billing_email = fieldValues.billing_email ? "" : "This field is required."
        if ('billing_phone' in fieldValues)
            temp.billing_phone = fieldValues.billing_phone ? "" : "This field is required."
        if ('billing_phone' in fieldValues)
            temp.billing_phone = fieldValues.billing_phone ? "" : "This field is required."
        if ('billing_postal_code' in fieldValues)
            temp.billing_postal_code = fieldValues.billing_postal_code ? "" : "This field is required."
        if ((fieldValues && fieldValues.billing_postal_code && fieldValues.billing_postal_code.length < 3) ||
            (fieldValues && fieldValues.billing_postal_code && fieldValues.billing_postal_code === null))
            temp.billing_postal_code = "Billing postal code required minimum 3 characters"
        if (fieldValues && fieldValues.billing_postal_code && fieldValues.billing_postal_code.match(/[^0-9a-z-\s]/i)) {
            temp.billing_postal_code = "Postal code only accepts alpha numeric"
        }
        if ('billing_state_code' in fieldValues)
            temp.billing_state_code = fieldValues.billing_state_code ? "" : "This field is required."

        setErrors({
            ...temp
        })

        if (fieldValues === values)
            return Object.values(temp).every(x => x === "")
    }
    const {
        values,
        setValues,
        errors,
        setErrors,
        handleInputChange,
    } = useForm(initialFValues, validate, true);

    const handleSubmit = e => {
        e.preventDefault()
        if (validate()) {
            if (addressbilling && addressbilling.data && addressbilling.data._id) {
                setCheckFormSubmit(true)
                const idvalue = addressbilling && addressbilling.data && addressbilling.data._id
                dispatch(updateAddressBilling(profile && profile.id,idvalue, values))
            } else {
                setCheckFormSubmit(true)
                const dm = { "status": true }
                const mergeobjectdata = Object.assign(values, dm);
                dispatch(storeAddressBilling(profile && profile.id,mergeobjectdata))
                setAlert(true)
            }
        }
    }

    useEffect(() => {
        dispatch(fetchCountry());
    }, [dispatch]);

    useEffect(() => {
        if (addressbilling && addressbilling.data && addressbilling.data.billing_country_code) {
            dispatch(fetchState(addressbilling && addressbilling.data && addressbilling.data.billing_country_code));
        }
    }, [addressbilling && addressbilling.data && addressbilling.data.billing_country_code]);

    const filteredCountryData = country.data.filter(({ code }) => code );

    const onSelectCountryChange = (countryCode) => {
        setValues({
          ...values,
          billing_country_code: countryCode,
        });
        setErrors({ billing_country_code: "" });
        dispatch(fetchState(countryCode));
      };
    const getSelectedCountry = (country_code) => {
        let filteredValue = { code: "", name: "" };
        filteredCountryData.map((key) => {
          if (key.code == country_code) {
            filteredValue = key;
          }
        });
         return filteredValue;
      };
    
      const onSelectStateChange = (currentstate) => {
        setValues({
          ...values,
          billing_state_code: currentstate,
        });
        setErrors({ billing_state_code: "" });
      };
    
      const getSelectedState = (currentstate) => {
        let filteredStateValue = { code: "", name: "" };
        let tempState = "";
        state &&
          state.data &&
          state.data.map((key) => {
            if (key.code == currentstate && tempState == "") {
              filteredStateValue = key;
              tempState = "success";
            }
          });
        return filteredStateValue;
      };

    return (
        <Box className="sc--settings--card" sx={{ width: '100%', boxSizing: 'border-box', p: 2.5 }}>
            {alert && (
                <Snackbar
                    open={open}
                    anchorOrigin={{
                        vertical: "top",
                        horizontal: "right"
                    }}
                    autoHideDuration={6000}
                    onClose={handleClose}
                    sx={{ mt: 2 }}
                >
                    <Alert 
                        severity={addressbilling && addressbilling.hasErrors === true ? "error" : "success"}
                        onClose={handleClose}
                        variant="filled"
                        sx={{ 
                            width: '100%', 
                            borderRadius: '12px',
                            fontWeight: 600,
                            boxShadow: '0 10px 15px -3px rgba(0,0,0,0.05)',
                            fontSize: '13px',
                            alignItems: 'center'
                        }}
                    >
                        {addressbilling && addressbilling.message}
                    </Alert>
                </Snackbar>
            )}

            <Card 
                className="sc--cardhead--details"
                sx={{ 
                    width: '100%',
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E2E8F0',
                    borderRadius: '16px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.02)',
                    overflow: 'hidden'
                }}
            >
                <CardHeader 
                    title={
                        <Typography variant="h6" component="h1" sx={{ fontWeight: 800, color: '#0F172A', fontSize: '16px', m: 0 }}>
                            Billing Address
                        </Typography>
                    } 
                    sx={{ p: 2.5, backgroundColor: '#FFFFFF' }}
                />
                
                {((country && country.loading === true) || (addressbilling && addressbilling.loading)) && (
                    <LinearProgress sx={{ height: '3px', backgroundColor: '#E2E8F0', '& .MuiLinearProgress-bar': { backgroundColor: '#1976d2' } }} />
                )}
                
                <Divider sx={{ borderColor: '#E2E8F0' }} />
                
                <CardContent sx={{ p: 4, boxSizing: 'border-box' }} component="form" onSubmit={handleSubmit} noValidate>
                    <Grid container spacing={3}>
                        
                        {/* 1. Name Entry Module Grid Box */}
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ minHeight: '40px' }}>
                                <TextField
                                    label="Name*"
                                    name="billing_name"
                                    type="text"
                                    size="small"
                                    value={values.billing_name}
                                    onChange={handleInputChange}
                                    inputProps={{ maxLength: 40 }}
                                    error={!!errors.billing_name}
                                    helperText={errors.billing_name}
                                    variant="outlined"
                                    sx={enterpriseBillingInputStyles}
                                />
                            </Box>
                        </Grid>

                        {/* 2. Corporate Name Entry Module Grid Box */}
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ minHeight: '40px' }}>
                                <TextField
                                    label="Company*"
                                    name="billing_company"
                                    type="text"
                                    size="small"
                                    value={values.billing_company}
                                    onChange={handleInputChange}
                                    inputProps={{ maxLength: 40 }}
                                    error={!!errors.billing_company}
                                    helperText={errors.billing_company}
                                    variant="outlined"
                                    sx={enterpriseBillingInputStyles}
                                />
                            </Box>
                        </Grid>

                        {/* 3. Telecom Coordinates Entry Module Grid Box */}
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ minHeight: '40px' }}>
                                <TextField
                                    label="Phone*"
                                    name="billing_phone"
                                    size="small"
                                    value={values.billing_phone}
                                    onChange={handleInputChange}
                                    inputProps={{ maxLength: 20 }}
                                    error={!!errors.billing_phone}
                                    helperText={errors.billing_phone}
                                    variant="outlined"
                                    sx={enterpriseBillingInputStyles}
                                />
                            </Box>
                        </Grid>

                        {/* 4. Electronic Mail Coordinates Entry Module Grid Box */}
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ minHeight: '40px' }}>
                                <TextField
                                    label="Email*"
                                    name="billing_email"
                                    type="email"
                                    size="small"
                                    value={values.billing_email}
                                    inputProps={{ maxLength: 60 }}
                                    onChange={handleInputChange}
                                    error={!!errors.billing_email}
                                    helperText={errors.billing_email}
                                    variant="outlined"
                                    sx={enterpriseBillingInputStyles}
                                />
                            </Box>
                        </Grid>

                        {/* 5. Primary Street Destination Line Module Grid Box */}
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ minHeight: '40px' }}>
                                <TextField
                                    label="Address1*"
                                    name="billing_address"
                                    size="small"
                                    value={values.billing_address}
                                    inputProps={{ maxLength: 35 }}
                                    onChange={handleInputChange}
                                    error={!!errors.billing_address}
                                    helperText={errors.billing_address}
                                    variant="outlined"
                                    sx={enterpriseBillingInputStyles}
                                />
                            </Box>
                        </Grid>

                        {/* 6. Secondary Suite Navigation Line Module Grid Box */}
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ minHeight: '40px' }}>
                                <TextField
                                    label="Address2"
                                    name="billing_address2"
                                    size="small"
                                    value={values.billing_address2}
                                    inputProps={{ maxLength: 35 }}
                                    onChange={handleInputChange}
                                    error={!!errors.billing_address2}
                                    helperText={errors.billing_address2}
                                    variant="outlined"
                                    sx={enterpriseBillingInputStyles}
                                />
                            </Box>
                        </Grid>

                        {/* 7. Postal Code Localization Identifier Grid Box */}
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ minHeight: '40px' }}>
                                <TextField
                                    label="Postal Code*"
                                    name="billing_postal_code"
                                    size="small"
                                    value={values.billing_postal_code}
                                    onChange={handleInputChange}
                                    inputProps={{ maxLength: 10 }}
                                    error={!!errors.billing_postal_code}
                                    helperText={errors.billing_postal_code}
                                    variant="outlined"
                                    sx={enterpriseBillingInputStyles}
                                />
                            </Box>
                        </Grid>

                        {/* 8. Regional Municipality Core Identifier Grid Box */}
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ minHeight: '40px' }}>
                                <TextField
                                    label="City*"
                                    name="billing_city"
                                    size="small"
                                    value={values.billing_city}
                                    inputProps={{ maxLength: 30 }}
                                    onChange={handleInputChange}
                                    error={!!errors.billing_city}
                                    helperText={errors.billing_city}
                                    variant="outlined"
                                    sx={enterpriseBillingInputStyles}
                                />
                            </Box>
                        </Grid>

                        {/* 9. Sovereign Territory Country Autocomplete Field Grid Box */}
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ minHeight: '40px' }}>
                                <Autocomplete
                                    className="help-text-error"
                                    disablePortal
                                    size="small"
                                    name="billing_country_code"
                                    id="country-select-demo"
                                    options={filteredCountryData}
                                    autoHighlight
                                    loading={country && country.loading}
                                    getOptionLabel={(option) => option.name}
                                    value={getSelectedCountry(values && values.billing_country_code)}
                                    sx={{
                                        width: '100%',
                                        '& .MuiAutocomplete-inputRoot': enterpriseBillingInputStyles['& .MuiOutlinedInput-root'],
                                        '& .MuiInputLabel-root': enterpriseBillingInputStyles['& .MuiInputLabel-outlined'],
                                        '& input': enterpriseBillingInputStyles['& input']
                                    }}
                                    renderOption={(props, option) => (
                                        <MenuItem {...props} value={option.code} key={option.code} sx={{ fontSize: '13.5px', fontWeight: 500, color: '#1E293B' }}>
                                            {option.icon} {option.name}
                                        </MenuItem>
                                    )}
                                    onChange={(event, value) => {
                                        if (value !== null) {
                                            onSelectCountryChange(value.code);
                                            setErrors({ billing_country_code: "" });
                                        } else {
                                            onSelectCountryChange("");
                                            setErrors({
                                                billing_country_code: "This field is required",
                                            });
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Country*"
                                            variant="outlined"
                                            error={!!errors.billing_country_code}
                                            helperText={errors.billing_country_code}
                                            sx={{ '& .MuiFormHelperText-root': enterpriseBillingInputStyles['& .MuiFormHelperText-root'] }}
                                        />
                                    )}
                                />
                            </Box>
                        </Grid>

                        {/* 10. Secondary Administrative State Autocomplete Field Grid Box */}
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ minHeight: '40px' }}>
                                <Autocomplete
                                    className="help-text-error"
                                    disablePortal
                                    size="small"
                                    name="billing_state_code"
                                    id="state-select-demo"
                                    options={state && state.data || []}
                                    autoHighlight
                                    loading={(state && state.loading) || (country && country.loading)}
                                    getOptionLabel={(option) => option.name || ""}
                                    value={getSelectedState(values && values.billing_state_code)}
                                    sx={{
                                        width: '100%',
                                        '& .MuiAutocomplete-inputRoot': enterpriseBillingInputStyles['& .MuiOutlinedInput-root'],
                                        '& .MuiInputLabel-root': enterpriseBillingInputStyles['& .MuiInputLabel-outlined'],
                                        '& input': enterpriseBillingInputStyles['& input']
                                    }}
                                    renderOption={(props, option) => (
                                        <MenuItem {...props} value={option.code} key={option.code} sx={{ fontSize: '13.5px', fontWeight: 500, color: '#1E293B' }}>
                                            {option.name}
                                        </MenuItem>
                                    )}
                                    onChange={(event, value) => {
                                        if (value !== null) {
                                            onSelectStateChange(value.code);
                                        } else {
                                            onSelectStateChange("");
                                            setErrors({ billing_state_code: "This field is required" });
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="State*"
                                            variant="outlined"
                                            error={!!errors.billing_state_code}
                                            helperText={errors.billing_state_code}
                                            sx={{ '& .MuiFormHelperText-root': enterpriseBillingInputStyles['& .MuiFormHelperText-root'] }}
                                        />
                                    )}
                                />
                            </Box>
                        </Grid>

                        {/* 11. Transaction Submission Core Execution Action Bottom Control Box */}
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', pt: 2, borderTop: '1px solid #E2E8F0', mt: 1 }}>
                                <Button 
                                    variant="contained" 
                                    onClick={handleSubmit}
                                    sx={{
                                        textTransform: 'none',
                                        borderRadius: '10px',
                                        fontWeight: 700,
                                        fontSize: '13.5px',
                                        height: '38px',
                                        px: 4,
                                        boxShadow: 'none',
                                        backgroundColor: '#1976d2',
                                        color: '#FFFFFF',
                                        '&:hover': { backgroundColor: '#115293', boxShadow: 'none' }
                                    }}
                                >
                                    Save Address
                                </Button>
                            </Box>
                        </Grid>

                    </Grid>
                </CardContent>
            </Card>
        </Box>
    );
};

export default AddressBillingPage;